/**
 * Brand + accent theming for the calculator widgets.
 *
 * IMPORTANT: Tailwind scans this file (see `content` in tailwind.config.js) and
 * only emits classes it can find as complete literal strings. Building them at
 * runtime -- `from-${accent}-700` -- leaves the JIT compiler with nothing to
 * match, and the styles vanish from the production bundle while still looking
 * correct in the source. Every class below is therefore spelled out in full.
 *
 * To rebrand: change `accent` to any key in `palettes`, and `name` to the
 * wordmark you want in the widget headers. That is the whole job.
 */

const palettes = {
  // Neutral default -- deliberately unbranded, suitable for demos and resale.
  slate: {
    pageBg: 'bg-gradient-to-br from-slate-50 to-slate-100',
    headerBar: 'bg-gradient-to-r from-slate-700 to-slate-900',
    inputFocus1: 'focus:ring-1 focus:ring-slate-500 focus:border-slate-500',
    inputFocus2: 'focus:ring-2 focus:ring-slate-500 focus:border-slate-500',
    toggleActive: 'bg-slate-700 text-white',
    tabInactive: 'bg-white text-slate-700 border border-slate-700',
    primaryButton: 'bg-slate-700 hover:bg-slate-800 text-white',
    panelStrong: 'bg-slate-100 border border-slate-300',
    panelSoft: 'bg-slate-50 border border-slate-200',
    panelHighlight: 'bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-slate-300',
    headingAccent: 'text-slate-900',
    borderAccent: 'border-slate-200',
    iconAccent: 'text-slate-600 hover:text-slate-700',
  },

  blue: {
    pageBg: 'bg-gradient-to-br from-blue-50 to-blue-100',
    headerBar: 'bg-gradient-to-r from-blue-700 to-blue-900',
    inputFocus1: 'focus:ring-1 focus:ring-blue-500 focus:border-blue-500',
    inputFocus2: 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
    toggleActive: 'bg-blue-700 text-white',
    tabInactive: 'bg-white text-blue-700 border border-blue-700',
    primaryButton: 'bg-blue-700 hover:bg-blue-800 text-white',
    panelStrong: 'bg-blue-100 border border-blue-300',
    panelSoft: 'bg-blue-50 border border-blue-200',
    panelHighlight: 'bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300',
    headingAccent: 'text-blue-900',
    borderAccent: 'border-blue-200',
    iconAccent: 'text-blue-600 hover:text-blue-700',
  },

  // The original client palette, kept so the previous look is one word away.
  red: {
    pageBg: 'bg-gradient-to-br from-red-50 to-red-100',
    headerBar: 'bg-gradient-to-r from-red-700 to-red-900',
    inputFocus1: 'focus:ring-1 focus:ring-red-500 focus:border-red-500',
    inputFocus2: 'focus:ring-2 focus:ring-red-500 focus:border-red-500',
    toggleActive: 'bg-red-700 text-white',
    tabInactive: 'bg-white text-red-700 border border-red-700',
    primaryButton: 'bg-red-700 hover:bg-red-800 text-white',
    panelStrong: 'bg-red-100 border border-red-300',
    panelSoft: 'bg-red-50 border border-red-200',
    panelHighlight: 'bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-300',
    headingAccent: 'text-red-900',
    borderAccent: 'border-red-200',
    iconAccent: 'text-red-600 hover:text-red-700',
  },
};

export const brand = {
  /** Wordmark shown above the calculator title. Set to null to hide it entirely. */
  name: 'Mortgage Tools',
  /** Any key in `palettes` above. */
  accent: 'slate',
};

export const theme = palettes[brand.accent];

export default theme;
